import * as Yup from 'yup';

import {
	seasonChanged,
	selectCurrentSeason,
} from 'features/rules/redux/rules-slice';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { useApolloClient } from '@apollo/client';
import RouterPromptModal from 'components/RouterPromptModal/RouterPromptModal';
import { TwoPaneLayout } from 'components/two-pane-layout/two-pane-layout';
import { usePropertyContext } from 'context/propertyContext';
import { alertAdded } from 'features/alerts/redux/alerts-slice';
import {
	GetPropSeasonsDocument,
	useUpdateSeasonMutation,
} from 'features/rules/seasons/gql/_gen_/prop-seasons.gql';
import { useUser } from 'features/users/context/userContext';
import { AutopilotOption } from 'graphql/gql-types';
import type { PropSeason } from 'graphql/gql-types';
import { useApolloError } from 'hooks/useApolloError';
import { labels } from 'locales/en.label';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import { FormButtons } from '../form-buttons/form-buttons';
import { RuleTabs } from '../rule-tabs/rule-tabs';
import { AutopilotForm } from './autopilot-form';
import { AutopilotInfo } from './autopilot-info';

const AutopilotFormSchema = z.object({
	priorityThresholdHigh: z.string(),
	priorityThresholdMed: z.string(),
	priorityThresholdLow: z.string(),
	autopilotHigh: z.nativeEnum(AutopilotOption).default(AutopilotOption.N),
	autopilotMed: z.nativeEnum(AutopilotOption).default(AutopilotOption.N),
	autopilotLow: z.nativeEnum(AutopilotOption).default(AutopilotOption.N),
	includeIntradayHigh: z.boolean().default(false),
	includeIntradayMed: z.boolean().default(false),
	includeIntradayLow: z.boolean().default(false),
});

export type AutopilotFormValues = z.infer<typeof AutopilotFormSchema>;

const autopilotLabels = labels.rules.autopilot_tab;

const autopilotSchema = Yup.object().shape({
	priorityThresholdHigh: Yup.number().moreThan(
		Yup.ref('priorityThresholdMed'),
		autopilotLabels.threshold_high_err,
	),
});

export const deriveIntradayAutopilotOption = (
	regularAutopilot: AutopilotOption,
	includeIntraday: boolean,
): AutopilotOption => {
	if (!includeIntraday || regularAutopilot === AutopilotOption.N) {
		return AutopilotOption.N;
	}

	return regularAutopilot === AutopilotOption.P
		? AutopilotOption.P
		: AutopilotOption.Y;
};

export const Autopilot = () => {
	const history = useHistory();
	const dispatch = useAppDispatch();
	const { user } = useUser();
	const {
		property: { propertyId },
	} = usePropertyContext();
	const { handleApolloError } = useApolloError();
	const selectedSeason = useAppSelector(selectCurrentSeason);
	const apolloClient = useApolloClient();

	const [updateSeason, { loading }] = useUpdateSeasonMutation({
		variables: {
			updatedBy: user?.login_id,
		},
		refetchQueries: ['GetPropSeasons'],
		onError: (error) => handleApolloError(error),
	});

	const [continueClicked, setContinueClicked] = useState(false);

	const initialValues: AutopilotFormValues = {
		priorityThresholdHigh: `${selectedSeason?.priority_threshold_high ?? 0}`,
		priorityThresholdMed: `${selectedSeason?.priority_threshold_med ?? 0}`,
		priorityThresholdLow: `${selectedSeason?.priority_threshold_low ?? 0}`,
		autopilotHigh: selectedSeason?.autopilot_high ?? AutopilotOption.N,
		autopilotMed: selectedSeason?.autopilot_med ?? AutopilotOption.N,
		autopilotLow: selectedSeason?.autopilot_low ?? AutopilotOption.N,
		includeIntradayHigh:
			(selectedSeason?.autopilot_high ?? AutopilotOption.N) !==
				AutopilotOption.N &&
			(selectedSeason?.autopilot_intraday_high ?? AutopilotOption.N) !==
				AutopilotOption.N,
		includeIntradayMed:
			(selectedSeason?.autopilot_med ?? AutopilotOption.N) !==
				AutopilotOption.N &&
			(selectedSeason?.autopilot_intraday_med ?? AutopilotOption.N) !==
				AutopilotOption.N,
		includeIntradayLow:
			(selectedSeason?.autopilot_low ?? AutopilotOption.N) !==
				AutopilotOption.N &&
			(selectedSeason?.autopilot_intraday_low ?? AutopilotOption.N) !==
				AutopilotOption.N,
	};

	const handleSubmit = async (values: AutopilotFormValues) => {
		const parsedValues = AutopilotFormSchema.parse(values);
		const {
			priorityThresholdHigh,
			priorityThresholdMed,
			priorityThresholdLow,
			autopilotHigh,
			autopilotMed,
			autopilotLow,
			includeIntradayHigh,
			includeIntradayMed,
			includeIntradayLow,
		} = parsedValues;

		const autopilotIntradayHigh = deriveIntradayAutopilotOption(
			autopilotHigh,
			includeIntradayHigh,
		);
		const autopilotIntradayMed = deriveIntradayAutopilotOption(
			autopilotMed,
			includeIntradayMed,
		);
		const autopilotIntradayLow = deriveIntradayAutopilotOption(
			autopilotLow,
			includeIntradayLow,
		);

		let success = false;

		// Fetch fresh season data to avoid overwriting rates with stale Redux state
		const { data: seasonsData } = await apolloClient.query({
			query: GetPropSeasonsDocument,
			variables: { propertyId },
			fetchPolicy: 'network-only',
		});
		const currentSeason = seasonsData?.getPropSeasons?.find(
			(s: PropSeason) => s.season_id === selectedSeason?.season_id,
		) as PropSeason | undefined;

		const seasonData = currentSeason ?? selectedSeason;

		await updateSeason({
			variables: {
				updatedSeason: {
					propertyId,
					priorityThresholdHigh: Number.parseFloat(priorityThresholdHigh),
					priorityThresholdMed: Number.parseFloat(priorityThresholdMed),
					priorityThresholdLow: Number.parseFloat(priorityThresholdLow),
					seasonId: seasonData?.season_id,
					seasonStartDate: seasonData?.season_start!,
					seasonEndDate: seasonData?.season_end!,
					selectedDays: seasonData?.selected_days!,
					derivedRateOffset: seasonData?.derived_rate_offset,
					derivedRateType: seasonData?.derived_rate_type,
					pricingStrategy: seasonData?.pricing_strategy,
					minRate: seasonData?.min_rate,
					maxRate: seasonData?.max_rate,
					use_proxy: seasonData?.use_proxy,
					proxy_type: seasonData?.proxy_type,
					proxy_offset_amt: seasonData?.proxy_offset_amt,
					proxy_comp_id: seasonData?.proxy_comp_id,
					clearDefaultConfig: true,
					autopilotHigh,
					autopilotMed,
					autopilotLow,
					autopilotIntradayHigh,
					autopilotIntradayMed,
					autopilotIntradayLow,
				},
			},
			onCompleted: () => {
				success = true;
				dispatch(alertAdded('success', autopilotLabels.save_success_msg));
				dispatch(
					seasonChanged({
						...selectedSeason!,
						priority_threshold_high: Number.parseFloat(priorityThresholdHigh),
						priority_threshold_med: Number.parseFloat(priorityThresholdMed),
						priority_threshold_low: Number.parseFloat(priorityThresholdLow),
						autopilot_high: values.autopilotHigh,
						autopilot_med: values.autopilotMed,
						autopilot_low: values.autopilotLow,
						is_default_config: false,
						autopilot_intraday_high: autopilotIntradayHigh,
						autopilot_intraday_med: autopilotIntradayMed,
						autopilot_intraday_low: autopilotIntradayLow,
					}),
				);
				if (continueClicked) history.push('/rules/seasons');
			},
			onError: (error) => {
				dispatch(alertAdded('error', error.message));
				setContinueClicked(false);
			},
		});

		return success;
	};

	return (
		<div>
			<RuleTabs
				loading={loading}
				showDefaultBadges={selectedSeason?.is_default_config === true}
			/>
			<Formik<AutopilotFormValues>
				initialValues={initialValues}
				validationSchema={autopilotSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ dirty, validateForm, submitForm }) => (
					<>
						<RouterPromptModal isBlocked={dirty && !continueClicked} />
						<Form>
							<TwoPaneLayout
								loading={loading}
								LeftSideComponent={<AutopilotForm />}
								RightSideComponent={<AutopilotInfo />}
							/>
							<FormButtons
								loading={loading}
								dirty={dirty}
								onContinueClick={async () => {
									if (!dirty) {
										history.push('/rules/seasons');
										return;
									}
									const isValid =
										Object.keys(await validateForm()).length === 0;
									if (isValid) setContinueClicked(true);
									submitForm();
								}}
							/>
						</Form>
					</>
				)}
			</Formik>
		</div>
	);
};
